import bcrypt from 'bcryptjs';

// Salt rounds for password hashing
const SALT_ROUNDS = 12;

// Simple encryption key for admin visibility (in production, use environment variables)
const ADMIN_KEY = 'dsh_admin_2024';

/**
 * Encrypt a password using bcrypt
 * @param {string} password - Plain text password
 * @returns {Promise<string>} - Hashed password
 */
export const encryptPassword = async (password) => {
  try {
    const salt = await bcrypt.genSalt(SALT_ROUNDS);
    const hashedPassword = await bcrypt.hash(password, salt);
    return hashedPassword;
  } catch (error) {
    console.error('Error encrypting password:', error);
    throw new Error('Failed to encrypt password');
  }
};

/**
 * Verify a password against its hash
 * @param {string} password - Plain text password to verify
 * @param {string} hashedPassword - Hashed password from database
 * @returns {Promise<boolean>} - True if password matches, false otherwise
 */
export const verifyPassword = async (password, hashedPassword) => {
  try {
    const isMatch = await bcrypt.compare(password, hashedPassword);
    return isMatch;
  } catch (error) {
    console.error('Error verifying password:', error);
    return false;
  }
};

/**
 * Check if a password is already hashed
 * @param {string} password - Password to check
 * @returns {boolean} - True if password appears to be hashed
 */
export const isPasswordHashed = (password) => {
  // bcrypt hashes start with $2a$, $2b$, or $2y$ and are typically 60 characters long
  return password && password.startsWith('$2') && password.length === 60;
};

/**
 * Simple encryption for admin visibility (NOT for security)
 * @param {string} password - Plain text password
 * @returns {string} - Encrypted password for admin view
 */
export const encryptForAdmin = (password) => {
  try {
    // Simple XOR encryption with admin key
    let encrypted = '';
    for (let i = 0; i < password.length; i++) {
      const charCode = password.charCodeAt(i) ^ ADMIN_KEY.charCodeAt(i % ADMIN_KEY.length);
      encrypted += String.fromCharCode(charCode);
    }
    return btoa(encrypted); // Base64 encode
  } catch (error) {
    console.error('Error encrypting for admin:', error);
    return password; // Fallback to plain text
  }
};

/**
 * Decrypt password for admin visibility
 * @param {string} encryptedPassword - Encrypted password from database
 * @returns {string} - Decrypted password
 */
export const decryptForAdmin = (encryptedPassword) => {
  try {
    // Check if it's a bcrypt hash first
    if (isPasswordHashed(encryptedPassword)) {
      return '[ENCRYPTED]'; // Don't try to decrypt bcrypt hashes
    }
    
    // Decode from base64
    const decoded = atob(encryptedPassword);
    
    // Simple XOR decryption
    let decrypted = '';
    for (let i = 0; i < decoded.length; i++) {
      const charCode = decoded.charCodeAt(i) ^ ADMIN_KEY.charCodeAt(i % ADMIN_KEY.length);
      decrypted += String.fromCharCode(charCode);
    }
    return decrypted;
  } catch (error) {
    console.error('Error decrypting for admin:', error);
    return encryptedPassword; // Return as-is if decryption fails
  }
}; 